import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';

interface GuidedTourProps {
  isActive: boolean;
  onComplete: () => void;
}

interface TourStep {
  target: string;
  title: string;
  description: string;
  position: 'top' | 'bottom' | 'left' | 'right';
}

const TOUR_STEPS: TourStep[] = [
  {
    target: '[data-tour="grid-view"]',
    title: 'Task Grid',
    description:
      'This is your task grid. Click any cell to edit inline. Use Tab to navigate between cells.',
    position: 'bottom',
  },
  {
    target: '[data-tour="gantt-toggle"]',
    title: 'Gantt Chart',
    description:
      'Switch to Gantt chart view to see your schedule visually with task bars and dependencies.',
    position: 'bottom',
  },
  {
    target: '[data-tour="ai-query"]',
    title: 'AI Assistant',
    description:
      'Press Cmd+K anytime to ask questions about your project in plain English.',
    position: 'bottom',
  },
  {
    target: '[data-tour="import-button"]',
    title: 'Import Projects',
    description:
      'Import existing schedules from MS Project XML, CSV, or Excel files.',
    position: 'bottom',
  },
  {
    target: '[data-tour="presence-avatars"]',
    title: 'Real-Time Collaboration',
    description:
      'See who else is viewing and editing this project right now.',
    position: 'left',
  },
];

const STORAGE_KEY = 'projectflow_tour_completed';

export function GuidedTour({ isActive, onComplete }: GuidedTourProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const step = TOUR_STEPS[currentStep];

  const measureTarget = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(step.target);
    if (el) {
      setTargetRect(el.getBoundingClientRect());
    } else {
      setTargetRect(null);
    }
  }, [step]);

  useEffect(() => {
    if (!isActive) return;
    measureTarget();
    window.addEventListener('resize', measureTarget);
    window.addEventListener('scroll', measureTarget, true);
    return () => {
      window.removeEventListener('resize', measureTarget);
      window.removeEventListener('scroll', measureTarget, true);
    };
  }, [isActive, measureTarget]);

  const handleComplete = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'true');
    onComplete();
  }, [onComplete]);

  const handleNext = useCallback(() => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      handleComplete();
    }
  }, [currentStep, handleComplete]);

  const handleSkip = useCallback(() => {
    handleComplete();
  }, [handleComplete]);

  if (!isActive || !step) return null;

  // Compute tooltip position
  const tooltipStyle: React.CSSProperties = {};
  const OFFSET = 12;

  if (targetRect) {
    switch (step.position) {
      case 'bottom':
        tooltipStyle.top = targetRect.bottom + OFFSET;
        tooltipStyle.left = targetRect.left + targetRect.width / 2;
        tooltipStyle.transform = 'translateX(-50%)';
        break;
      case 'top':
        tooltipStyle.bottom = window.innerHeight - targetRect.top + OFFSET;
        tooltipStyle.left = targetRect.left + targetRect.width / 2;
        tooltipStyle.transform = 'translateX(-50%)';
        break;
      case 'left':
        tooltipStyle.top = targetRect.top + targetRect.height / 2;
        tooltipStyle.right = window.innerWidth - targetRect.left + OFFSET;
        tooltipStyle.transform = 'translateY(-50%)';
        break;
      case 'right':
        tooltipStyle.top = targetRect.top + targetRect.height / 2;
        tooltipStyle.left = targetRect.right + OFFSET;
        tooltipStyle.transform = 'translateY(-50%)';
        break;
    }
  } else {
    // No target found, center the tooltip
    tooltipStyle.top = '50%';
    tooltipStyle.left = '50%';
    tooltipStyle.transform = 'translate(-50%, -50%)';
  }

  // Spotlight style (box-shadow trick on a positioned element)
  const spotlightStyle: React.CSSProperties = targetRect
    ? {
        position: 'fixed',
        top: targetRect.top - 4,
        left: targetRect.left - 4,
        width: targetRect.width + 8,
        height: targetRect.height + 8,
        borderRadius: 8,
        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
        zIndex: 9998,
        pointerEvents: 'none',
      }
    : {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 9998,
        pointerEvents: 'none',
      };

  return createPortal(
    <>
      {/* Spotlight overlay */}
      <div style={spotlightStyle} />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        style={{
          position: 'fixed',
          zIndex: 9999,
          ...tooltipStyle,
        }}
        className="max-w-xs rounded-lg border bg-popover text-popover-foreground shadow-lg p-4"
      >
        <p className="font-semibold text-sm">{step.title}</p>
        <p className="text-sm text-muted-foreground mt-1">
          {step.description}
        </p>

        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-muted-foreground">
            {currentStep + 1} / {TOUR_STEPS.length}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSkip}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Skip tour
            </button>
            <button
              onClick={handleNext}
              className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              {currentStep < TOUR_STEPS.length - 1 ? 'Next' : 'Finish'}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
